import React from 'react';
import pt from 'prop-types';
import { connect } from 'react-redux';
import {
  getEasyblocksPageTemplate,
  resolveAsyncResources,
} from '@swell/apps-sdk';

import api, { withRequestCache } from 'services/api';
import actions from 'actions';

import { locationWithQuery } from 'utils';
import { getLocalProxyStorefrontURL, getStorefrontURL } from 'utils/storefront';
import { getCookie } from 'utils/cookie';

import LoadingView from 'components/view/loading';
import NotFoundPage from 'components/pages/error/404';
import StorefrontEditorPage from 'components/storefront/editor';
import {
  getPages,
  findPage,
  getPageFile,
  getNewPageData,
  getPageId,
  getIdFromPageData,
  getSlugFromPageData,
  getPageUrl,
} from 'components/storefront/editor/utils/pages';
import {
  getSwell,
  getTheme,
  getThemeConfigsMap,
} from 'components/storefront/editor/utils/editor';
import {
  MESSAGE_EVENTS,
  postEasyblocksFocus,
  postEasyblocksCanvasPageRoute,
} from 'components/storefront/editor/utils/post-message';
import {
  SWELL_PAGE_PREFIX,
  SWELL_GLOBAL,
  SWELL_DEFAULT,
  parseItemPath,
  getCanvasElement,
  subscribeForm,
  getForm,
} from 'components/storefront/editor/utils/easyblocks';
import {
  getCanvasData,
  shouldFetchPageRecord,
  PAGE_RECORD_QUERY,
  PAGE_URL_PATTERN_REGEX,
} from 'components/storefront/editor/utils/page-data';
import {
  getConfigFile,
  stringifyFileData,
} from 'components/storefront/editor/utils/theme-config';
import { publishing } from 'components/storefront/editor/utils/storefront';

/** @typedef {import('@swell/apps-sdk').Swell} Swell */
/** @typedef {import('@swell/apps-sdk').SwellTheme} SwellTheme */

const DEFAULT_PAGE_ID = 'index';
const PUBLISH_CHECK_INTERVAL = 3000;

@connect(
  (state) => ({
    client: state.client,
    user: state.user,
    loading: state.loading,
    storefront: state.storefronts.storefront,
    storefrontConfigs: state.storefronts.configs,
    storefronts: state.storefronts,
    themeConfigs: state.storefronts.themeConfigs,
    session: state.session,
    lookup: state.lookup,
    settings: state.settings,
  }),
  (dispatch) => ({
    loadAllStorefronts: () => dispatch(actions.storefronts.loadAll()),

    loadStorefront: (id) => dispatch(actions.storefronts.load(id)),

    fetchStorefront: (id) => dispatch(actions.storefronts.fetch(id)),

    updateStorefront: (id, data) =>
      dispatch(actions.storefronts.update(id, data)),

    loadThemeConfigs: (id) => {
      return dispatch(actions.storefronts.loadThemeConfigs(id));
    },

    fetchThemeConfigs(id, branchId, query) {
      return dispatch(
        actions.storefronts.fetchThemeConfigs(id, branchId, query),
      );
    },

    fetchThemeConfig: (id, file_path, branchId = null) => {
      return dispatch(
        actions.storefronts.fetchThemeConfig(id, file_path, branchId),
      );
    },

    createThemeConfig(data) {
      return dispatch(actions.storefronts.createThemeConfig(data));
    },

    updateThemeConfig(id, data) {
      return dispatch(actions.storefronts.updateThemeConfig(id, data));
    },

    deleteThemeConfig: (id) => {
      return dispatch(actions.storefronts.deleteThemeConfig(id));
    },

    getInstalledApps(storefront) {
      return dispatch(actions.storefronts.getInstalledApps(storefront));
    },

    loadAppConfigFile: (appId, file_path) => {
      return dispatch(actions.apps.loadConfigFile(appId, file_path));
    },

    fetchStorefrontMenus: (id) => {
      return dispatch(actions.storefronts.fetchMenus(id));
    },
  }),
)
export class StorefrontThemeEditor extends React.PureComponent {
  static propTypes = {
    location: pt.object,
    router: pt.object,
    client: pt.object,
    user: pt.object,
    storefront: pt.object,
    settings: pt.object,
    session: pt.object,
    params: pt.object.isRequired,
    loadAllStorefronts: pt.func.isRequired,
    loadStorefront: pt.func.isRequired,
    fetchStorefront: pt.func.isRequired,
    updateStorefront: pt.func.isRequired,
    loadThemeConfigs: pt.func.isRequired,
    fetchThemeConfigs: pt.func.isRequired,
    createThemeConfig: pt.func.isRequired,
    updateThemeConfig: pt.func.isRequired,
    deleteThemeConfig: pt.func.isRequired,
    getInstalledApps: pt.func.isRequired,
    loadAppConfigFile: pt.func.isRequired,
    fetchStorefrontMenus: pt.func.isRequired,
  };

  static contextTypes = {
    showLoading: pt.func.isRequired,
    hideLoading: pt.func.isRequired,
    openModal: pt.func.isRequired,
    notifySuccess: pt.func.isRequired,
    notifyError: pt.func.isRequired,
  };

  state = {
    pages: null,
    page: null,
    // Requested page id
    pageId: '',
    pageQuery: null,
    pageRecord: null,
    canvasData: null,
    sectionGroups: [],
    focusedField: SWELL_DEFAULT,
    loaded: false,
    canvasLoaded: false,
    editorConfig: null,
    error: null,
    viewport: 'fit-screen',
    /** @type {Swell | null} */
    swellClient: null,
    /** @type {SwellTheme | null} */
    swellTheme: null,
    onChangeViewport: this.onChangeViewport.bind(this),
    onChangePageRecord: this.onChangePageRecord.bind(this),
    onUpdateStorefrontSettings: this.onUpdateStorefrontSettings.bind(this),
    onError: this.onError.bind(this),
    onAddPage: this.onAddPage.bind(this),
    onDuplicatePage: this.onDuplicatePage.bind(this),
    onDeletePage: this.onDeletePage.bind(this),
    onPublish: this.onPublish.bind(this),
    onSaveThemeLanguage: this.onSaveThemeLanguage.bind(this),
    onAfterSave: this.onAfterSave.bind(this),
    updatePage: this.updatePage.bind(this),
    replacePage: this.replacePage.bind(this),
    updateThemeConfig: this.updateThemeConfig.bind(this),
  };

  /** @type {AbortController | null} */
  eventController = null;

  formId = null;

  unsubscribeForm = null;

  publishInterval = null;

  /** @type {HTMLIFrameElement | null} */
  canvasElement = null;

  componentDidMount() {
    const { loadAllStorefronts, themeConfigs } = this.props;

    this.addEventListeners();

    // TODO: support branchId
    this.setState({ themeConfigs: themeConfigs[this.getThemeId()] });

    loadAllStorefronts().then(async () => {
      await this.fetch();

      this.startPublishPooling();
    });
  }

  componentDidUpdate(prevProps) {
    const {
      params,
      location: { query },
      themeConfigs,
    } = this.props;
    const { page } = this.state;

    const prevQuery = prevProps.location.query;

    if (themeConfigs !== prevProps.themeConfigs) {
      this.setState({ themeConfigs: themeConfigs[this.getThemeId()] });
    }

    if (params.id !== prevProps.params.id) {
      this.fetch();
    } else if (!query.page) {
      this.ensureQueryPage();
    } else if (page && query !== prevQuery) {
      const hasPageChanged =
        query.page !== page.id && query.page !== this.state.pageId;

      const hasProxyChanged =
        (query.proxy || prevQuery.proxy) && query.proxy !== prevQuery.proxy;

      const shouldReloadFrame = hasPageChanged || hasProxyChanged;

      if (hasProxyChanged && this.state.swellClient) {
        this.setState((state) => {
          state.swellClient.storefront_url = this.getStorefrontURL();

          return {
            ...state,
            loaded: false,
            editorConfig: null,
            pageRecord: null,
          };
        });
      }

      if (shouldReloadFrame) {
        // Mark that we are already requesting the page to avoid circular requests
        this.setState({ pageId: query.page });

        this.reloadEditorFrame();
      }
    }
  }

  componentWillUnmount() {
    if (this.eventController) {
      this.eventController.abort();
    }

    this.stopPublishPooling();
  }

  addEventListeners() {
    this.eventController = new AbortController();

    window.addEventListener('message', this.eventListener, {
      signal: this.eventController.signal,
    });
  }

  /** @param {MessageEvent} event */
  eventListener = (event) => {
    const { type, payload } = event.data;

    switch (type) {
      case MESSAGE_EVENTS.EASYBLOCKS.CANVAS_PAGE_ROUTE:
        return this.changePageRoute(payload.pageId);

      case MESSAGE_EVENTS.EASYBLOCKS.FOCUS:
        return this.onFocus(payload.target);

      case MESSAGE_EVENTS.EASYBLOCKS.BACKEND.DOCUMENT.UPDATED:
        return this.onBackendDocumentUpdated();

      case MESSAGE_EVENTS.EASYBLOCKS.ITEM_INSERTED:
        return this.onEasyblocksItemInserted(payload);

      case MESSAGE_EVENTS.STOREFRONT_EDITOR.CANVAS.MOUNTED:
        return this.onCanvasMounted();

      case MESSAGE_EVENTS.STOREFRONT_EDITOR.CANVAS.LOADED:
        return this.onCanvasLoaded();

      case MESSAGE_EVENTS.STOREFRONT_EDITOR.CANVAS.RELOAD:
        return this.reloadEditorFrame();

      case MESSAGE_EVENTS.STOREFRONT_EDITOR.NOTIFY_ERROR:
        this.context.notifyError(payload.message);
        break;

      default:
        return;
    }
  };

  inCodeEditor() {
    const { location } = this.props;

    return location.pathname.endsWith('/code');
  }

  ensureQueryPage() {
    const { router, location } = this.props;
    const { query } = location;

    if (!query.page && !this.inCodeEditor()) {
      router.replace(
        locationWithQuery(location, { page: DEFAULT_PAGE_ID }, true),
      );
    }
  }

  getThemeId() {
    return this.props.storefront?.theme_id;
  }

  async fetch() {
    const { params, loadStorefront, fetchStorefrontMenus } = this.props;

    this.setState({ loaded: false });

    await loadStorefront(params.id);
    await fetchStorefrontMenus(params.id);

    await withRequestCache(async () => {
      await this.loadThemeConfigs();
      if (this.inCodeEditor()) {
        this.setState({
          loaded: true,
        });
      } else {
        return this.fetchEditorConfig();
      }
    });
  }

  async loadThemeConfigs() {
    const { loadThemeConfigs } = this.props;
    const themeId = this.getThemeId();

    if (!themeId) {
      return;
    }

    // Get the entire list of configs from the redux store
    const themeConfigs = await loadThemeConfigs(themeId);

    this.setState((state) => {
      // Update theme configs for the SwellTheme instance
      if (state.swellTheme) {
        state.swellTheme.themeConfigs = getThemeConfigsMap(themeConfigs);
      }

      return { ...state };
    });

    return themeConfigs;
  }

  fetchThemeConfigs() {
    const { fetchThemeConfigs } = this.props;
    const themeId = this.getThemeId();

    return fetchThemeConfigs(themeId);
  }

  async fetchPages() {
    const { editorConfig } = this.state;

    const themeConfigs = await this.loadThemeConfigs();
    if (!themeConfigs) {
      return;
    }

    const pages = await getPages(editorConfig, themeConfigs);

    this.setState({ pages });
  }

  async fetchEditorConfig() {
    const { location } = this.props;
    const { themeConfigs } = this.state;
    const { page: pageId, record: recordId } = location.query;

    try {
      const { swellClient, swellTheme } = await this.getSwellClientAndTheme();
      swellTheme.clearFormData();

      await swellTheme.initGlobals(pageId);

      const globalEditorConfig =
        this.state.editorConfig ||
        (await this.getGlobalEditorConfig(swellTheme));

      const pageEditorConfig = await this.getPageEditorConfig(
        pageId,
        swellTheme,
      );

      const editorConfig = {
        ...globalEditorConfig,
        ...pageEditorConfig,
      };

      if (!editorConfig.globals) {
        this.onError('Could not load storefront editor configuration.');
        return;
      }

      const pages = await getPages(editorConfig, themeConfigs);
      const page = this.getPage(pageId, pages);
      const hasPageRoute = Boolean(page?.url);

      if (!hasPageRoute) {
        return this.onError(
          `Page ${pageId} route is not defined by the storefront.`,
        );
      }

      const pageRecord = await this.getPageRecord(page, recordId);
      const canvasData = await getCanvasData(
        swellClient,
        swellTheme,
        page,
        pageRecord,
      );

      editorConfig.layoutSectionGroups = canvasData.layoutSectionGroups;

      this.setCanvasData(canvasData);

      this.setState({
        loaded: true,
        editorConfig,
        pages,
        page,
        pageRecord,
        canvasData,
        sectionGroups: canvasData.sectionGroups,
      });
    } catch (err) {
      if (err instanceof Error) {
        err.message = `StorefrontThemeEditor: ${err.message}`;
      }

      console.error(err);
      this.onError('Unable to load theme editor configuration.');
    }
  }

  async ensureStorefrontLocalization(swellClient) {
    const store = await swellClient.storefront.settings.get('store');
    const { locale: currentLocale, currency: currentCurrency } =
      swellClient.getStorefrontLocalization();
    const cookieLocale = getCookie('swell-locale');
    const cookieCurrency = getCookie('swell-currency');
    const locale =
      typeof cookieLocale === 'string' ? cookieLocale : store.locale || 'en-US';
    const currency =
      typeof cookieCurrency === 'string'
        ? cookieCurrency
        : store.currency || 'USD';

    if (typeof locale === 'string' && locale !== currentLocale) {
      await swellClient.storefront.locale.select(locale);
    }

    if (typeof currency === 'string' && currency !== currentCurrency) {
      await swellClient.storefront.currency.select(currency);
    }
  }

  async createSwellClient() {
    const { client, storefront, loadAppConfigFile } = this.props;
    const { showLoading, hideLoading } = this.context;

    const config = await loadAppConfigFile(storefront.app_id, 'swell.json');

    const shopifyCompatibilityConfig = await loadAppConfigFile(
      storefront.app_id,
      'shopify_compatibility.json',
    );

    return getSwell(
      client,
      storefront,
      JSON.parse(config),
      JSON.parse(shopifyCompatibilityConfig),
      {
        resourceLoadingIndicator: (loading) =>
          loading ? showLoading() : hideLoading(),
      },
    );
  }

  async getSwellClient() {
    const swellClient =
      this.state.swellClient || (await this.createSwellClient());

    swellClient.storefront_url = this.getStorefrontURL();

    await this.ensureStorefrontLocalization(swellClient);

    return swellClient;
  }

  async createSwellTheme(swellClient) {
    const { storefront, getInstalledApps } = this.props;
    const { themeConfigs } = this.state;
    const { storefrontApp } = await getInstalledApps(storefront);

    return getTheme(
      swellClient,
      { themeConfigs },
      storefrontApp.app.storefront?.theme?.resources,
      storefrontApp.app.storefront?.theme?.forms,
    );
  }

  getSwellTheme(swellClient) {
    const { swellTheme } = this.state;

    return swellTheme || this.createSwellTheme(swellClient);
  }

  async getSwellClientAndTheme() {
    const swellClient = await this.getSwellClient();
    const swellTheme = await this.getSwellTheme(swellClient);

    this.setState({ swellClient, swellTheme });

    return { swellClient, swellTheme };
  }

  getStorefrontURL() {
    const { session, location, storefront } = this.props;

    const shouldUseProxy =
      session?.local_proxy?.storefront_id === storefront.id &&
      (!location.query.proxy || location.query.proxy === 'local');

    return shouldUseProxy
      ? getLocalProxyStorefrontURL(session)
      : getStorefrontURL(storefront, false, true);
  }

  /** @param {SwellTheme} swellTheme */
  async getGlobalEditorConfig(swellTheme) {
    const allSections = await swellTheme.getAllSections();

    return { allSections };
  }

  /**
   * @param {string} pageId
   * @param {SwellTheme} swellTheme
   */
  async getPageEditorConfig(pageId, swellTheme) {
    const pageTemplate = await getEasyblocksPageTemplate(swellTheme, pageId);

    const pageSections =
      typeof pageTemplate === 'object'
        ? await swellTheme
            .getPageSections(pageTemplate, false)
            .then(resolveAsyncResources)
        : [];

    return {
      pageTemplate,
      pageSections,
      globals: this.getEditorThemeGlobals(swellTheme),
    };
  }

  /** @param {SwellTheme} theme */
  getEditorThemeGlobals(theme) {
    const { settings, menus, page, shopify_compatibility, configs } =
      theme.globals || {};

    const { editor, translations, theme: themeJson, presets } = configs;

    return {
      settings,
      menus,
      page,
      storefrontConfig: theme.props, // TODO: rename this for editor
      shopify_compatibility,
      configs: {
        editor,
        translations,
        theme: themeJson,
        presets,
      },
    };
  }

  getThemeConfigsMap() {
    const themeConfigs = new Map();

    for (const config of this.state.themeConfigs?.results ?? []) {
      themeConfigs.set(config.file_path, config);
    }

    return themeConfigs;
  }

  getAllPages(pages = this.state.pages) {
    return pages ? [...pages.templates, ...pages.pages] : null;
  }

  getPage(pageId, pages = this.state.pages) {
    const allPages = this.getAllPages(pages);
    const hasPages = allPages?.length > 0;

    if (!pageId || !hasPages) {
      return null;
    }

    return findPage(allPages, 'id', pageId);
  }

  getPageConfig(pageId) {
    const { themeConfigs, swellTheme } = this.state;
    const pageConfigPath = swellTheme.getPageConfigPath(pageId);
    const hasPath = pageConfigPath?.length > 0;
    const config =
      hasPath &&
      themeConfigs.results.find(
        (config) =>
          Boolean(config.file_data) && config.file_path === pageConfigPath,
      );

    if (!config) {
      throw new Error(`Unable to find page config (${pageConfigPath})`);
    }

    return config;
  }

  makePageQuery(pageConfig) {
    const query = { ...PAGE_RECORD_QUERY };
    const expand = new Set();

    if (pageConfig.url) {
      const parts = pageConfig.url.match(PAGE_URL_PATTERN_REGEX);

      if (parts !== null) {
        for (const item of parts) {
          const part = item.slice(1);
          const pos = part.indexOf('.');

          if (pos !== -1) {
            const link = part.slice(0, pos);

            expand.add(link);
          }
        }
      }
    }

    if (expand.size > 0) {
      query.expand = [...expand.values()];
      delete query.fields;
    }

    return query;
  }

  fetchPageRecord(pageConfig, id) {
    const pageQuery = this.makePageQuery(pageConfig);

    this.setState({ pageQuery });

    return api.get(
      `/data/${pageConfig.collection}/${id || ':last'}`,
      pageQuery,
    );
  }

  async getPageRecord(pageConfig, id) {
    const { router, location } = this.props;

    if (!shouldFetchPageRecord(pageConfig)) {
      router.replace(locationWithQuery(location, { record: undefined }, true));
      return null;
    }

    let record = await this.fetchPageRecord(pageConfig, id);

    if (!record && id) {
      // Try a default record instead of one provided
      record = await this.fetchPageRecord(pageConfig);
    }

    if (!record) {
      router.replace(locationWithQuery(location, { record: undefined }, true));
      return null;
    }

    router.replace(locationWithQuery(location, { record: record.id }, true));

    return record.slug || record.id ? record : null;
  }

  async reloadEditorFrame() {
    await this.reloadCanvas();
    this.fetchEditorConfig();
  }

  changePageRoute(pageId) {
    const { location, router } = this.props;

    this.setState({ focusedField: SWELL_DEFAULT }, () =>
      router.push(locationWithQuery(location, { page: pageId }, true)),
    );
  }

  setInitialFocus() {
    const activeElement = window.location.hash?.slice(1);

    postEasyblocksFocus(activeElement || SWELL_DEFAULT);
  }

  async reloadCanvas() {
    await new Promise((resolve) =>
      this.setState({ canvasLoaded: false, canvasData: null }, resolve),
    );

    const canvasElement = this.getCanvasElement();

    if (canvasElement) {
      return new Promise((resolve) => {
        canvasElement.onload = resolve;
        // eslint-disable-next-line no-self-assign
        canvasElement.src = canvasElement.src;
      });
    }
  }

  onCanvasMounted() {
    this.canvasElement = getCanvasElement();
    this.setCanvasData();

    window.editorWindowAPI.onUpdate = this.onUpdateEditorAPI.bind(this);
  }

  onCanvasLoaded() {
    this.setState({ canvasLoaded: true }, () => {
      this.setInitialFocus();
    });
  }

  onUpdateEditorAPI() {
    this.updateForm();
  }

  updateForm() {
    const form = getForm();

    if (!form || form.id === this.formId) {
      return;
    }

    if (this.unsubscribeForm) {
      this.unsubscribeForm();
      this.unsubscribeForm = null;
    }

    this.formId = form.id;
    this.unsubscribeForm = subscribeForm();

    this.setCanvasData();
    this.setInitialFocus();
  }

  getCanvasElement() {
    return this.canvasElement || getCanvasElement();
  }

  setCanvasData(canvasData = this.state.canvasData) {
    const canvasElement = this.getCanvasElement();

    if (!canvasElement?.contentWindow?.setCanvasData || !canvasData) {
      return;
    }

    const {
      fetchThemeConfigs,
      createThemeConfig,
      updateThemeConfig,
      storefront,
    } = this.props;

    const { contentWindow } = canvasElement;

    contentWindow.setCanvasData({
      ...canvasData,
      themeId: storefront.theme_id,
      branchId: null,
      fetchThemeConfigs,
      createThemeConfig,
      updateThemeConfig,
    });
  }

  getFocusedField(target) {
    const focusedField = typeof target !== 'string' ? SWELL_DEFAULT : target;

    switch (focusedField) {
      case SWELL_GLOBAL:
      case SWELL_PAGE_PREFIX:
      case SWELL_DEFAULT:
        return focusedField;
      default:
        break;
    }

    const [sectionId, blockId] = parseItemPath(focusedField);

    return blockId || sectionId || SWELL_DEFAULT;
  }

  onFocus(target) {
    const {
      canvasLoaded,
      viewport,
      focusedField: currentFocusedField,
    } = this.state;

    if (!canvasLoaded) {
      return;
    }

    const focusedField = this.getFocusedField(target);

    window.location.hash = focusedField;

    if (focusedField === currentFocusedField) {
      return;
    }

    if (viewport === 'expand' && focusedField !== SWELL_DEFAULT) {
      this.setState({ viewport: 'fit-screen' });
    }

    this.setState({
      focusedField,
    });
  }

  onChangeViewport(viewport) {
    const { focusedField } = this.state;

    this.setState({ viewport }, () => {
      // Reset focus for the 'expand' viewport.
      // Focus reset delay prevents animation from jerking.
      if (viewport === 'expand' && focusedField !== SWELL_DEFAULT) {
        setTimeout(() => postEasyblocksFocus(SWELL_DEFAULT), 300);
      }
    });
  }

  async onChangePageRecord(record) {
    const { router, location } = this.props;
    const { page, swellClient, swellTheme } = this.state;

    this.setState({ pageRecord: record });

    router.replace(locationWithQuery(location, { record: record.id }, true));

    await this.reloadCanvas();

    const canvasData = await getCanvasData(
      swellClient,
      swellTheme,
      page,
      record,
    );

    this.setCanvasData(canvasData);
    this.setState({ canvasData, canvasLoaded: true });
  }

  async onUpdateStorefrontSettings(collection) {
    const { swellClient, swellTheme, editorConfig } = this.state;

    await swellClient.getStorefrontSettings(true);

    const globals = {};

    switch (collection) {
      case 'menus':
        globals.menus = await swellTheme.resolveMenuSettings();
        break;
      default:
        break;
    }

    swellTheme.setGlobals(globals);
    editorConfig.globals = this.getEditorThemeGlobals(swellTheme);

    this.setState({
      editorConfig,
    });
  }

  onError(message) {
    this.setState({
      loaded: true,
      error: message,
    });
  }

  async createPage(pageData) {
    const { notifyError } = this.context;
    const { storefront, createThemeConfig } = this.props;
    const { theme_id: themeId } = storefront;

    try {
      const id = getIdFromPageData(pageData);
      const slug = getSlugFromPageData(pageData);
      const file = getPageFile(slug, pageData);

      const result = await createThemeConfig({
        parent_id: themeId,
        preview: true,
        type: 'theme',
        name: slug,
        file,
        file_path: `theme/templates/${id}.json`,
      });

      return result;
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    }
  }

  async updatePage(pageData) {
    const { notifyError } = this.context;

    try {
      const id = getIdFromPageData(pageData);
      const config = this.getPageConfig(id);
      const result = await this.updateThemeConfig(config.id, pageData);
      return result;
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    }
  }

  async deletePage(id) {
    const { notifyError } = this.context;
    const { deleteThemeConfig } = this.props;

    try {
      const config = this.getPageConfig(id);
      const result = await deleteThemeConfig(config.id);
      return result;
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    }
  }

  async replacePage(currentPageId, pageData) {
    const createdPage = await this.createPage(pageData);

    if (!createdPage) {
      return;
    }

    return this.deletePage(currentPageId);
  }

  async onAddPage(values) {
    const pageData = getNewPageData(values);
    const result = await this.createPage(pageData);

    if (!result) {
      return false;
    }

    await this.fetchPages({ id: result.id });

    return true;
  }

  async onDuplicatePage(page) {
    const { notifyError } = this.context;

    try {
      const config = this.getPageConfig(page.id);
      const fileData = JSON.parse(config.file_data);
      const slug = `${config.name}-1`;
      const id = getPageId(slug);
      const name = `${page.label} (copy)`;
      const pageData = {
        ...fileData,
        page: {
          slug,
          label: name,
          url: getPageUrl(slug),
          description: fileData.page?.description,
        },
        id,
      };

      const result = await this.createPage(pageData);

      await this.fetchPages({ id: result.id });

      // redirect to the duplicated page
      if (result) {
        postEasyblocksCanvasPageRoute(id);
      }
    } catch (error) {
      notifyError(error.message);
    }
  }

  async onDeletePage(id) {
    const {
      page: { id: pageId },
    } = this.state;

    const result = await this.deletePage(id);

    if (!result) {
      return;
    }

    await this.fetchPages({ id: result.id });

    // redirect to the default when deleting the current page
    if (id === pageId) {
      postEasyblocksCanvasPageRoute(DEFAULT_PAGE_ID);
    }
  }

  updateThemeConfig(configId, configData) {
    const { updateThemeConfig } = this.props;
    const { themeConfigs } = this.state;

    const themeConfig = themeConfigs.results.find(
      (config) => config.id === configId,
    );

    if (!themeConfig) {
      throw new Error(`Theme config not found:${configId}`);
    }

    return updateThemeConfig(configId, {
      file: {
        data:
          configData &&
          themeConfig.file_path.endsWith('.json') &&
          typeof configData !== 'string'
            ? stringifyFileData(configData)
            : configData,
      },
    });
  }

  onEasyblocksItemInserted(payload) {
    // mark added item to be focused after it is rendered
    const target = `${payload.name}.${payload.index}`;
    this.setState({ addedField: target });
  }

  onBackendDocumentUpdated() {
    // focus previously added field
    const { addedField } = this.state;
    if (addedField) {
      this.setState({ addedField: undefined }, () =>
        setTimeout(() => this.onFocus(addedField), 300),
      );
    }
  }

  async onAfterSave({ updatedConfigIds, isPageUpdated, redirectPageId }) {
    const { fetchStorefront, params } = this.props;

    await fetchStorefront(params.id);

    if (!isPageUpdated) {
      return this.loadThemeConfigs();
    }

    let query;

    if (updatedConfigIds.length > 0) {
      query = { id: { $in: updatedConfigIds } };
    }

    await this.fetchPages(redirectPageId ? undefined : query);

    if (redirectPageId) {
      postEasyblocksCanvasPageRoute(redirectPageId);
    }
  }

  checkPublish = async () => {
    const { params, fetchStorefront } = this.props;
    const storefront = await fetchStorefront(params.id);

    if (!publishing(storefront)) {
      this.stopPublishPooling();
    }
  };

  startPublishPooling() {
    const { storefront } = this.props;

    if (this.publishInterval || !publishing(storefront)) {
      return;
    }

    this.publishInterval = setInterval(
      this.checkPublish,
      PUBLISH_CHECK_INTERVAL,
    );
  }

  stopPublishPooling() {
    if (this.publishInterval) {
      clearInterval(this.publishInterval);
      this.publishInterval = null;
    }
  }

  async onPublish() {
    const { params, user, storefront, updateStorefront, fetchStorefront } =
      this.props;

    await updateStorefront(storefront.id, {
      $publish: true,
      $published_user_id: user.id,
    });
    await fetchStorefront(params.id);

    this.startPublishPooling();
  }

  async onSaveThemeLanguage(configs) {
    const { storefront } = this.props;
    const { swellTheme } = this.state;
    const { theme_id: themeId } = storefront;

    const { batch, shouldCreate, updatedConfigIds } = configs.reduce(
      (acc, config) => {
        if (!config.id) {
          acc.shouldCreate = true;
          acc.batch.push({
            method: 'post',
            url: `/:themes:configs`,
            data: {
              parent_id: themeId,
              type: 'theme',
              name: config.name,
              file: getConfigFile(config.name, config.file_data),
              file_path: config.file_path,
            },
          });

          return acc;
        }

        const existingConfig = swellTheme.themeConfigs.get(config.file_path);

        if (existingConfig && existingConfig.file_data !== config.file_data) {
          acc.updatedConfigIds.push(config.id);
          acc.batch.push({
            method: 'put',
            url: `/:themes:configs/${config.id}`,
            data: { file: config.file_data },
          });
        }

        return acc;
      },
      { shouldCreate: false, batch: [], updatedConfigIds: [] },
    );

    await api.post('/data/:batch', batch);

    await this.fetchThemeConfigs();

    // non-blocking
    this.reloadEditorFrame();

    return true;
  }

  onClickErrorTryAgain = (event) => {
    event.preventDefault();
    this.setState({ loaded: false }, () => this.fetch());
  };

  render() {
    const { storefront } = this.props;
    const { loaded, canvasLoaded } = this.state;

    if (!loaded) {
      return <LoadingView />;
    }

    if (!storefront) {
      return <NotFoundPage />;
    }

    const inCodeEditor = this.inCodeEditor();

    return (
      <StorefrontEditorPage
        {...this.context}
        {...this.props}
        {...this.state}
        loading={!canvasLoaded && !inCodeEditor}
        onClickErrorTryAgain={this.onClickErrorTryAgain}
        inCodeEditor={inCodeEditor}
      />
    );
  }
}

export default StorefrontThemeEditor;
